// src/components/SpotSelect.js
import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { API_BASE_URL } from '../config';

/**
 * SpotSelect Component
 * 
 * A reusable dropdown component for selecting spots.
 * Automatically loads spots from the API and handles selection.
 * 
 * @param {string} value - Currently selected spotId
 * @param {function} onChange - Callback when selection changes. Receives full spot object or null
 * @param {boolean} disabled - Whether the select is disabled
 * @param {string} error - Error message to display
 * @param {string} placeholder - Placeholder text
 * @param {boolean} isClearable - Whether the select can be cleared
 * @param {string} className - Additional CSS classes
 * @param {string} label - Label text (optional)
 * @param {boolean} required - Whether the field is required (adds red asterisk)
 */
export default function SpotSelect({
  value,
  onChange,
  disabled = false,
  error = null,
  placeholder = 'スポットを選択してください',
  isClearable = true,
  className = '',
  label = null,
  required = false,
}) {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load spots on component mount
  useEffect(() => {
    const loadSpots = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSpots(data.items || []);
        }
      } catch (e) {
        console.error('Failed to load spots:', e);
      } finally {
        setLoading(false);
      }
    };
    loadSpots();
  }, []);

  // Memoize options for react-select
  const options = useMemo(
    () =>
      spots.map((s) => ({
        value: s.spotId,
        label: s.spotName,
        // Store full spot object for onChange callback
        spot: s,
      })),
    [spots]
  );

  // Find currently selected option
  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  // Handle selection change
  const handleChange = (opt) => {
    if (onChange) {
      // Pass full spot object or null
      onChange(opt?.spot || null);
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="form-label">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
      )}
      <Select
        options={options}
        value={selectedOption}
        onChange={handleChange}
        isDisabled={disabled || loading}
        isClearable={isClearable}
        placeholder={loading ? '読み込み中…' : placeholder}
        classNamePrefix="react-select"
        className={error ? 'is-invalid' : ''}
        styles={{
          control: (base) => ({
            ...base,
            borderColor: error ? '#dc3545' : base.borderColor,
          }),
        }}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}
